import React, { useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography } from '@mui/material';
import {
  ExportProgress,
  Phase,
} from 'app/features/study/hooks/useStudyExportDownload';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface DownloadProgressOverlayProps {
  phase: Phase;
  progress: ExportProgress | null;
}

const ACTIVE_BLUE = '#4aa3ff';
const SUCCESS_GREEN = '#4ade80';
const BG = '#1a1f2e';
const TRACK = '#2c3242';
const TEXT = '#e6e8ee';
const SUB = '#98a0b0';

const findButtonNode = (): HTMLElement | null => {
  const candidates: HTMLElement[] = [
    ...Array.from(document.querySelectorAll<HTMLElement>('#download')),
    ...Array.from(document.querySelectorAll<HTMLElement>('[data-cy="descargar"]')),
    ...Array.from(document.querySelectorAll<HTMLElement>('[data-cy="download"]')),
  ];
  return candidates.find((el) => el.offsetParent !== null) ?? candidates[0] ?? null;
};

const useButtonRect = (active: boolean): Rect | null => {
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    const measure = () => {
      const node = findButtonNode();
      if (!node) {
        setRect(null);
        return;
      }
      const r = node.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();

    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    document.addEventListener('evodicom.toolbar.resize', onChange);

    let frames = 0;
    let rafId: number;
    const loop = () => {
      frames += 1;
      measure();
      if (frames < 30) {
        rafId = requestAnimationFrame(loop);
      }
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
      document.removeEventListener('evodicom.toolbar.resize', onChange);
      cancelAnimationFrame(rafId);
    };
  }, [active]);

  return rect;
};

const subtitleFor = (phase: Phase): string => {
  if (phase === 'reconnecting') return 'Reconectando';
  if (phase === 'completing') return 'Listo';
  return 'Descargando';
};

const DownloadProgressOverlay: React.FC<DownloadProgressOverlayProps> = ({ phase, progress }) => {
  const visible = phase !== 'idle';
  const rect = useButtonRect(visible);

  if (!visible || !rect) return null;

  const percent = progress?.percent ?? 0;
  const subtitle = subtitleFor(phase);
  const isComplete = phase === 'completing';
  const color = isComplete ? SUCCESS_GREEN : ACTIVE_BLUE;

  const overlay = (
    <Box
      data-testid="download-progress-overlay"
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }}
      sx={{
        backgroundColor: BG,
        color: TEXT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        zIndex: 9999,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Typography
        data-testid="download-progress-percent"
        sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color }}
      >
        {percent}%
      </Typography>
      <Typography
        data-testid="download-progress-subtitle"
        sx={{ fontSize: 10, color: SUB, mt: 0.25 }}
      >
        {subtitle}
      </Typography>
      <Box
        data-testid="download-progress-bar-track"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 4,
          backgroundColor: TRACK,
        }}
      >
        <Box
          data-testid="download-progress-bar-fill"
          style={{ width: `${percent}%` }}
          sx={{
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
    </Box>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default DownloadProgressOverlay;
